import { createReducer } from '@reduxjs/toolkit';

import { ResolutionState } from '../components/ConversationResolution/types';
import { elevioActions } from '../elevio.actions';
import { composeArticleContentFromMessages } from '../utils/messages';

export const initialResolutionState: ResolutionState = {
  stage: 'initial',
  selectedArticleId: null,
  selectedArticleTitle: null,
  articleDropdownOpen: false,
  suggestedArticleIds: [],
  articleSearchInputValue: '',
  articleSearchTerm: '',
};

export const resolutionReducer = createReducer(
  initialResolutionState,
  (builder) => {
    builder
      .addCase(elevioActions.resolution.initiateResolutionPrompt, (state) => {
        state.stage = 'prompt';
      })
      .addCase(elevioActions.resolution.setSolution, (state, action) => {
        state.stage = 'solution';
        state.articleSolved = true;
        state.draft = undefined;
        state.articleSearchInputValue = action.payload.searchInputValue; // pre-fill the input with the same term as the user was searching for in the sidepanel
        state.articleSearchTerm = action.payload.searchInputValue; // pre-fetch articles based off the same term as the user was searching for in the sidepanel
      })
      .addCase(elevioActions.resolution.setSolutionDropdownOpen, (state) => {
        state.articleDropdownOpen = true;
      })
      .addCase(
        elevioActions.resolution.debouncedSetSolutionDropdownClosed,
        (state) => {
          state.articleDropdownOpen = false;
        },
      )
      .addCase(elevioActions.resolution.setSolutionSubmitted, (state) => {
        state.articleDropdownOpen = initialResolutionState.articleDropdownOpen;
        state.suggestedArticleIds = initialResolutionState.suggestedArticleIds;
        state.articleSearchTerm = initialResolutionState.articleSearchTerm;
        state.articleSolved = initialResolutionState.articleSolved;
        state.draft = initialResolutionState.draft;
        state.previousState = initialResolutionState.previousState;
        state.selectedArticleId = initialResolutionState.selectedArticleId;
        state.selectedArticleTitle =
          initialResolutionState.selectedArticleTitle;
        state.stage = 'solutionSubmitted';
      })
      .addCase(elevioActions.resolution.setCancelWarning, (state) => {
        state.previousState = {
          stage: state.stage,
        };
        state.stage = 'cancelWarning';
      })
      .addCase(elevioActions.resolution.unsetCancelWarning, (state) => {
        if (!state.previousState) return;
        state.stage = state.previousState.stage;
        state.previousState = undefined;
      })
      .addCase(elevioActions.resolution.setNoSolution, (state) => {
        state.stage = 'noSolution';
        state.articleSolved = false;
        state.selectedArticleId = null;
      })
      .addCase(
        elevioActions.resolution.setNoSolutionNoDraftArticle,
        (state) => {
          state.stage = 'noSolutionNoDraftArticle';
          state.draft = false;
        },
      )
      .addCase(elevioActions.resolution.setNoSolutionSubmitted, (state) => {
        state.stage = 'noSolutionSubmitted';
      })
      .addCase(
        elevioActions.resolution.setNoSolutionDraftArticle,
        (state, action) => {
          state.stage = 'noSolutionDraftArticle';
          state.draft = {
            notes: '',
            question: action.payload.conversationSubject,
            ticket: action.payload.url,
            articleContent: composeArticleContentFromMessages(
              action.payload.messages,
            ),
          };
        },
      )
      .addCase(
        elevioActions.resolution.setNoSolutionDraftArticleSubmitted,
        (state) => {
          state.stage = 'noSolutionDraftArticleSubmitted';
        },
      )
      // Solution Article actions
      .addCase(
        elevioActions.resolution.setResolutionArticleId,
        (state, action) => {
          state.stage = 'solutionArticleSelected';
          state.selectedArticleId = action.payload.articleId;
          state.selectedArticleTitle = action.payload.articleTitle;
          state.articleDropdownOpen = false;
        },
      )
      .addCase(elevioActions.resolution.unsetResolutionArticleId, (state) => {
        state.selectedArticleId = null;
        state.selectedArticleTitle = null;
      })
      .addCase(
        elevioActions.resolution.setResolutionDraftQuestion,
        (state, action) => {
          if (!state.draft) return;

          state.draft.question = action.payload.question;
        },
      )
      .addCase(
        elevioActions.resolution.setResolutionDraftNotes,
        (state, action) => {
          if (!state.draft) return;

          state.draft.notes = action.payload.notes;
        },
      )
      // Resolution suggested article Id's
      .addCase(
        elevioActions.resolution.setResolutionArticleIds,
        (state, action) => {
          state.suggestedArticleIds = action.payload.articleIds;
        },
      )
      .addCase(
        elevioActions.resolution.setResolutionInputValue,
        (state, action) => {
          state.articleSearchInputValue = action.payload.resolutionInputValue;
        },
      )
      .addCase(
        elevioActions.resolution.setResolutionSearchTerm,
        (state, action) => {
          state.articleSearchTerm = action.payload.resolutionSearchTerm;
        },
      )
      .addCase(elevioActions.resolution.ticketResolutionExited, (state) => {
        state.articleDropdownOpen = initialResolutionState.articleDropdownOpen;
        state.suggestedArticleIds = initialResolutionState.suggestedArticleIds;
        state.articleSearchInputValue =
          initialResolutionState.articleSearchInputValue;
        state.articleSearchTerm = initialResolutionState.articleSearchTerm;
        state.selectedArticleId = initialResolutionState.selectedArticleId;
        state.selectedArticleTitle =
          initialResolutionState.selectedArticleTitle;
        state.stage = initialResolutionState.stage;
        state.articleSolved = initialResolutionState.articleSolved;
        state.draft = initialResolutionState.draft;
        state.previousState = initialResolutionState.previousState;
      })
      .addCase(
        elevioActions.resolution.setResolutionArticleContent,
        (state, action) => {
          if (state.draft) {
            state.draft.articleContent = action.payload.articleContent;
          }
        },
      );
  },
);
